import React from 'react';
import moment from 'moment';
import Star from './star.svg';
import { PlanStage, BasePlan } from '@fullstory/proto/fs/services/orgs';
import { Flex, Box, Button } from '@fullstory/components';
import { useIntl } from 'react-intl';
import { PLAN_UPGRADE_ROUTE, generateRoutePath } from '@fullstory/routes';
import { fullstorySelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

interface UpgradeLinkProps {
  size?: React.ComponentProps<typeof Button>['size'];
  stage: PlanStage;
  basePlan: BasePlan;
  exp: number | null;
  now: number;
  showStar?: boolean;
}

const { getOrgId } = fullstorySelectors;

const UpgradeLink: React.FC<UpgradeLinkProps> = ({
  stage,
  basePlan,
  exp,
  now,
  showStar = true,
  size = 'x-small',
}) => {
  const intl = useIntl();
  const orgId = useSelector(getOrgId);
  const { pathname } = useLocation();

  const navigateToUpgradePage = () => {
    window.open('https://www.fullstory.com/plans/', '_blank');
  };

  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigateToUpgradePage();
  };

  let text: string;
  const upgradeMessage =
    basePlan == BasePlan.ENTERPRISE_PLAN
      ? intl.formatMessage({ defaultMessage: 'Plan Options', id: 'xh5189' })
      : intl.formatMessage({ defaultMessage: 'Upgrade Plan', id: '8Wfi09' });
  switch (stage) {
    case PlanStage.TRIAL_STAGE: {
      const expMoment = moment(exp);
      const nowMoment = moment(now);
      const daysLeft = expMoment.diff(nowMoment, 'days');
      if (daysLeft > 0) {
        text = intl.formatMessage(
          {
            defaultMessage: '{daysLeft, plural, one {# day} other {# days}} left in your trial',
            id: 'FZDH/9',
          },
          { daysLeft },
        );
      } else {
        console.error(`UpgradeLink: org is in trial, but exp date is in the past: ${exp}`);
        text = upgradeMessage;
      }
      break;
    }
    case PlanStage.EXPIRED_TRIAL_STAGE:
    case PlanStage.ACTIVE_STAGE:
    case PlanStage.CANCELLED_STAGE:
      text = upgradeMessage;
      break;
    default:
      console.error(`UpgradeLink: stage ${stage} is not valid for UpgradeLink`);
      return null;
  }

  return (
    <>
      <Flex alignItems="center" justifyContent="flex-end">
        {showStar && (
          <Box marginRight={8}>
            <Star />
          </Box>
        )}
        {pathname != generateRoutePath(PLAN_UPGRADE_ROUTE, { orgId }) && (
          <Box>
            <Button size={size} variant="fill-secondary" onClick={handleUpgradeClick}>
              {text}
            </Button>
          </Box>
        )}
      </Flex>
    </>
  );
};

export { UpgradeLink };
